import { typographyDesktop } from '@shared/tokens/typography.desktop.stylex';
import { typography } from '@shared/tokens/typography.stylex';
import { anatomy } from '@shared/tokens/anatomy.stylex';
import { colors } from '@shared/tokens/colors.stylex';
import { fonts } from '@shared/tokens/fonts.stylex';
import { InputSizes } from '../model/types';
import * as stylex from '@stylexjs/stylex';

type TSizes = {
  small: string;
  medium: string;
  large: string;
};

const sizes: TSizes = {
  small: typography['fontSizeCaption-1'],
  medium: typography['fontSizeBody-3'],
  large: typography['fontSizeBody-2'],
};

const inputTextLineHeight: TSizes = {
  large: typographyDesktop['lineHeightBody-2'],
  medium: typographyDesktop['lineHeightBody-4'],
  small: typographyDesktop['lineHeightCaption-1'],
};

const inputMsgLineHeight: TSizes = {
  large: typographyDesktop['lineHeightCaption-1'],
  medium: typographyDesktop['lineHeightCaption-2'],
  small: '12px',
};

const inputMsgFontSize: TSizes = {
  large: typographyDesktop['fontSizeCaption-1'],
  medium: typographyDesktop['fontSizeCaption-2'],
  small: '10px',
};

const labelHeight: TSizes = {
  large: '28px',
  medium: '20px',
  small: '16px',
};

const labelTop: TSizes = {
  large: '-32px',
  medium: '-23px',
  small: '-20px',
};

const labelLineHeight: TSizes = {
  large: typographyDesktop['fontSizeHeading-4'],
  medium: typographyDesktop['lineHeightCaption-1'],
  small: typographyDesktop['lineHeightCaption-2'],
};

const labelFontSize: TSizes = {
  large: typographyDesktop['fontSizeBody-4'],
  medium: typographyDesktop['fontSizeCaption-1'],
  small: '12px',
};

const searchIconsOffset = {
  large: { top: '18px', right: '18px' },
  medium: { top: '14px', right: '20px' },
  small: { top: '10px', right: '16px' },
};

const eyeIconOffset = {
  large: { top: '16px', right: '20px' },
  medium: { top: '12px', right: '20px' },
  small: { top: '10px', right: '16px' },
};

export const styles = stylex.create({
  container: {
    position: 'relative',
  },
  containerDisabled: {
    cursor: 'not-allowed',
  },
  base: {
    border: '1px solid',
    borderRadius: anatomy['size-8'],
    ':focus': {
      outline: 'none',
    },
    background: colors.primaryBackground,
    color: colors.primaryText,
    width: '100%',
  },
  isDisabled: {
    borderColor: colors.ghostBorder,
    background: colors.ghostBackground,
    '::placeholder': {
      color: colors.ghostText,
    },
    color: colors.ghostText,
    cursor: 'not-allowed',
    ':hover': {
      borderColor: 'none',
    },
  },
  isReadonly: {
    background: colors.ghostBackground,
    color: colors.foregroundTertiary,
    userSelect: 'none',
    ':hover': {
      borderColor: colors.tertiaryBorder,
    },
    ':focus': {
      borderColor: colors.tertiaryBorder,
    },
  },
  inputText: (size: InputSizes) => ({
    fontFamily: fonts.din2014Regular,
    lineHeight: inputTextLineHeight[size],
    fontWeight: typographyDesktop.fontWeighRegular,
    fontSize: sizes[size],
    paddingLeft: size === 'small' ? '16px' : '20px',
  }),
  inputMsgSize: (size: InputSizes) => ({
    fontFamily: fonts.din2014Regular,
    paddingTop: '3px',
    lineHeight: inputMsgLineHeight[size],
    fontSize: inputMsgFontSize[size],
  }),
  labelText: (size: InputSizes) => ({
    position: 'absolute',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    fontFamily: fonts.din2014Regular,
    color: colors.secondaryText,
    height: labelHeight[size],
    top: labelTop[size],
    lineHeight: labelLineHeight[size],
    fontSize: labelFontSize[size],
  }),
  inputMsgColor: (status) => ({
    color: status === 'success' ? colors.successText : colors.errorText,
  }),
  searchIcons: (size: InputSizes) => ({
    position: 'absolute',
    display: 'flex',
    gap: '8px',
    top: searchIconsOffset[size].top,
    right: searchIconsOffset[size].right,
  }),

  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'absolute',
  },
  eyeIcon: (size: InputSizes) => ({
    position: 'absolute',
    cursor: 'pointer',
    color: colors.primaryIcon,
    top: eyeIconOffset[size].top,
    right: eyeIconOffset[size].right,
  }),
  size: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
  },
  small: {
    width: '280px',
    height: anatomy['size-36'],
  },
  medium: {
    width: '280px',
    height: anatomy['size-48'],
  },
  large: {
    width: '280px',
    height: '60px',
  },
  success: {
    borderColor: colors.successBorder,
  },
  error: {
    borderColor: colors.errorBorder,
  },
  default: {
    borderColor: colors.tertiaryBorder,
    ':hover': {
      borderColor: colors.hoverBorder,
    },
    ':focus': {
      borderColor: colors.accentBorder,
    },
  },
  XMarksIcon: {
    cursor: 'pointer',
  },
});
